import { parseLambdaPayload } from "../../../utils/routerLambdaUtils";

/**
 * fetchActionItemsSeed - PURE async fetch (no React) for the one-time action-items seed used to
 * populate the tab's count badge as soon as a session is active. POSTs the meetingUuid to
 * /action-items and decodes the RouterLambda envelope via parseLambdaPayload — the same helper
 * useInsightPolling uses, so the contract lives in one place instead of two copies. Returns the
 * actionItems array (empty when the response carries none); throws on network/decode failure so
 * the calling hook owns loading/seed state and error logging.
 */
export const fetchActionItemsSeed = async (meetingUuid) => {
  const response = await fetch("/action-items", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ meetingUuid }),
  });
  const data = await parseLambdaPayload(response, "/action-items");
  return data.actionItems ?? [];
};

/**
 * Builds the secondary subtitle line for an action item: owner (or "Unassigned")
 * and deadline when present, joined by a pipe. e.g. "Priya Nair | Aug 12".
 */
export const buildSubtitle = (item) =>
  [item.ownerName || "Unassigned", item.deadline].filter(Boolean).join(" | ");

/**
 * detectedAtKey - numeric epoch (ms) used to order an action item for display. Keys on
 * detectedAt, falling back to updatedAt then amendedAt for rows missing detectedAt.
 */
export const detectedAtKey = (item) =>
  Date.parse(item?.detectedAt ?? item?.updatedAt ?? item?.amendedAt);

/**
 * sortByDetectedAtDesc - returns a NEW array ordered by detectedAt DESCENDING (newest first),
 * matching the store's prepend-newest intent. Display-only: never mutates the input (copies via
 * spread). A missing/unparseable timestamp yields NaN from Date.parse; we treat NaN as the OLDEST
 * possible value so those rows sink to the BOTTOM rather than jumping to the top of a DESC list.
 */
export const sortByDetectedAtDesc = (items) =>
  [...items].sort((a, b) => {
    const ka = detectedAtKey(a);
    const kb = detectedAtKey(b);
    if (Number.isNaN(ka) && Number.isNaN(kb)) return 0;
    if (Number.isNaN(ka)) return 1; // a has no timestamp → after b
    if (Number.isNaN(kb)) return -1; // b has no timestamp → after a
    return kb - ka; // descending: larger (newer) epoch first
  });

/**
 * buildItemFields - allowlists/normalizes the editable action-item fields (content, ownerName,
 * deadline) so the editable schema lives in one place and the create + amend helpers agree on it.
 * The edit form already trims content and nulls empty owner/deadline; this pins the shape to those
 * three keys and drops anything else a caller might pass.
 */
export const buildItemFields = ({ content, ownerName, deadline }) => ({
  content,
  ownerName,
  deadline,
});

/**
 * createActionItem - builds a full new action-item row from editable fields: a client-minted
 * itemId (stable across the optimistic row and the eventual server echo), detectedAt/updatedAt
 * both stamped to now, and amendmentCount seeded at 0.
 */
export const createActionItem = (fields) => {
  const now = new Date().toISOString();
  return {
    itemId: crypto.randomUUID(),
    detectedAt: now,
    updatedAt: now,
    amendmentCount: 0,
    ...buildItemFields(fields),
  };
};

/**
 * amendActionItem - builds the optimistic overlay for an edit: the itemId, the edited fields, and
 * a refreshed updatedAt. detectedAt is intentionally omitted so the store's merge preserves the
 * original detection time, and amendmentCount is left untouched (edits do not bump it today).
 */
export const amendActionItem = (itemId, fields) => ({
  itemId,
  ...buildItemFields(fields),
  updatedAt: new Date().toISOString(),
});
